import { useCallback, useMemo, useRef } from 'react';
import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { mapServerValidationErrors, type ServerFieldMap } from '@/shared/forms';
import {
  useCreateInventoryVehicle,
  useUpdateInventoryVehicle,
} from '@/api/generated/inventory-vehicles/inventory-vehicles';
import {
  buildVehicleFormSchema,
  emptyVehicleFormValues,
  type VehicleFormValues,
} from './vehicleFormSchema';
import { toVehicleRequestBody } from './toVehicleRequest';

/** Mapování názvů polí z BE validace na cesty polí ve formuláři. */
const SERVER_FIELD_MAP: ServerFieldMap = {
  Name: 'name',
  Plate: 'plate',
  OwnerOrganizationPartyId: 'owner',
  VehicleCategoryCbId: 'category',
  VehicleGroupCbId: 'group',
  VehicleManufacturerCbId: 'manufacturer',
  PassengerSeats: 'passengerSeats',
  TourLeaderSeats: 'tourLeaderSeats',
  InitialMileageKm: 'initialMileageKm',
  Vin: 'vin',
  CommissioningDate: 'commissioningDate',
  RetirementDate: 'retirementDate',
  PreferredDriverIds: 'preferredDrivers',
};

interface UseVehicleFormOptions {
  /** Bez `vehicleId` = zakládání, s `vehicleId` = editace. */
  vehicleId?: number;
  initialValues?: VehicleFormValues;
  /** Voláno po úspěšném uložení (předává id uloženého vozu) — invalidace, toast; NEzavírá okno. */
  onSaved: (id: number) => void;
  /** Voláno při chybě, kterou se nepodařilo namapovat na pole. */
  onError?: (error: unknown) => void;
}

/**
 * Formulář vozu: Zod + create/update mutace + mapování BE chyb. `submit()` = úspěch uložení.
 */
export function useVehicleForm({
  vehicleId,
  initialValues,
  onSaved,
  onError,
}: UseVehicleFormOptions) {
  const { t } = useTranslation('inventory');
  const schema = useMemo(() => buildVehicleFormSchema(t), [t]);

  const createMutation = useCreateInventoryVehicle();
  const updateMutation = useUpdateInventoryVehicle();
  const successRef = useRef(false);

  const form = useForm({
    defaultValues: initialValues ?? emptyVehicleFormValues(),
    validators: { onChange: schema },
    onSubmit: async ({ value, formApi }) => {
      successRef.current = false;
      const data = toVehicleRequestBody(value);
      try {
        if (vehicleId != null) {
          await updateMutation.mutateAsync({ id: vehicleId, data });
          onSaved(vehicleId);
        } else {
          const newId = await createMutation.mutateAsync({ data });
          onSaved(newId);
        }
        successRef.current = true;
      } catch (error) {
        const mapped = mapServerValidationErrors(formApi, error, SERVER_FIELD_MAP);
        if (!mapped) onError?.(error);
      }
    },
  });

  /** Spustí validaci + uložení; vrací `true` při úspěchu. */
  const submit = useCallback(async () => {
    await form.handleSubmit();
    return successRef.current;
  }, [form]);

  return {
    form,
    submit,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
  };
}

/** Typ instance TanStack Form pro detail vozu (pro propsy podřízených komponent). */
export type VehicleFormApi = ReturnType<typeof useVehicleForm>['form'];
